import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import styles from './Breadcrumbs.module.scss';

export interface Crumb {
  label: string;
  /** Omitted on the current page -- the last crumb is never a link. */
  href?: string;
}

interface BreadcrumbsProps {
  items: Crumb[];
}

/**
 * Services -> Practice -> Service.
 *
 * The trail exists because services are nested two levels deep now: without
 * it, the only way back up from a service page is the browser's back button
 * or the header. The last crumb names the current page and carries
 * aria-current, so it is announced as the location rather than as a link that
 * goes nowhere.
 *
 * Separators are decorative and hidden from assistive tech; the list markup
 * is what conveys the structure.
 */
const Breadcrumbs = ({ items }: BreadcrumbsProps) => {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={styles.breadcrumbs}>
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className={styles.item}>
                {item.href && !isLast ? (
                  <Link to={item.href}>{item.label}</Link>
                ) : (
                  <span aria-current="page">{item.label}</span>
                )}
              </li>
              {!isLast && (
                <li className={styles.separator} aria-hidden="true">
                  /
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
